import { CalendarDate } from '@internationalized/date';
import { debounce } from 'lodash';
import { isEmpty, sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Control, Controller, FieldError, useFormContext, UseFormRegister } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import { useJavascriptResolver } from 'dashboard/components/GridLayoutRender/computed-properties/useJavascriptResolver';
import PhoneInput from 'dashboard/components/PhoneInput';
import {
  InputSchemaType,
  TriggerAccount,
  TriggerAccountInputModalConfiguration,
} from 'dashboard/models/trigger';
import AddressInputAutocomplete, {
  AddressInputAutocompleteData,
} from 'dashboard/views/DashboardAccountsShowView/components/AddressInputAutocomplete';
import Button from 'design-system/components/Button';
import Checkbox from 'design-system/components/Checkbox';
import DateField from 'design-system/components/DatePicker/DateField';
import Input from 'design-system/components/Input';
import MaskedInput from 'design-system/components/MaskedInput';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import {
  ADDRESS_FORMATS_BY_COUNTRY_CODE,
  AddressField,
  DEFAULT_ADDRESS_FORMAT,
  DEFAULT_LABELS,
} from 'lib/constants/addresses';
import { COUNTRIES, CountryCode, US_STATES } from 'lib/constants/locales';
import isValidJSON from 'lib/utils/formValidation/isValidJSON';

const REQUIRED_FIELDS: Record<
  string,
  { fieldName: Partial<keyof AddressInputAutocompleteData>; requiredCountries: CountryCode[] }
> = {
  address1: {
    fieldName: 'street1',
    requiredCountries: [], // required for all countries
  },
  city: {
    fieldName: 'city',
    requiredCountries: ['US'],
  },
  postalCode: {
    fieldName: 'postalCode',
    requiredCountries: ['GB', 'US'],
  },
  subdivision: {
    fieldName: 'subdivision',
    requiredCountries: ['US'],
  },
};

interface FormAccountActionProps {
  accountDataContext: Record<string, unknown>;
  trigger: TriggerAccount;
  handleRunAction: (values: Record<string, unknown>) => Promise<void>;
  handleCancel: () => void;
}

const FormAccountAction = ({
  accountDataContext,
  trigger,
  handleRunAction,
  handleCancel,
}: FormAccountActionProps) => {
  const {
    control,
    register,
    watch,
    getValues,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useFormContext();

  const [triggerParams, setTriggerParams] = useState(getValues());

  useEffect(() => {
    const debouncedSetTriggerParams = debounce((triggerParams) => {
      setTriggerParams(triggerParams);
    }, 500);
    const subscription = watch(debouncedSetTriggerParams);

    return () => subscription.unsubscribe();
  }, [watch]);

  const formFields = trigger.attributes['input-modal-configuration'].map((inputSchema, idx) => {
    return (
      <AccountActionFormField
        key={idx}
        inputSchema={inputSchema}
        accountDataContext={accountDataContext}
        triggerParams={triggerParams}
        control={control}
        register={register}
        error={errors?.[inputSchema.key] as FieldError}
        isSubmitting={isSubmitting}
      />
    );
  });

  return (
    <form onSubmit={handleSubmit(handleRunAction)} data-test='form-account-action'>
      {formFields}

      <HStack equal={true}>
        <Button
          id='form-account-action__cancel'
          disabled={isSubmitting}
          variant='basic'
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          id='form-account-action__submit'
          disabled={isSubmitting}
          isPending={isSubmitting}
        >
          Submit
        </Button>
      </HStack>
    </form>
  );
};

interface AccountActionFormFieldProps {
  inputSchema: TriggerAccountInputModalConfiguration;
  accountDataContext: Record<string, unknown>;
  control: Control;
  error: FieldError;
  triggerParams: Record<string, unknown>;
  isSubmitting: boolean;
  register: UseFormRegister<Record<string, unknown>>;
}

const AccountActionFormField = ({
  inputSchema,
  accountDataContext,
  triggerParams,
  register,
  control,
  error,
  isSubmitting,
}: AccountActionFormFieldProps) => {
  const globals = { ...accountDataContext, triggerParams };

  const [isResolvingHidden, resolvedIsHidden] = useJavascriptResolver(
    inputSchema.hidden,
    globals,
    'false',
  );

  const [isResolvingOptions, resolvedOptions] = useJavascriptResolver(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    inputSchema.options,
    globals,
    '[]',
  );

  const [isResolvingIsRequired, resolvedIsRequired] = useJavascriptResolver(
    inputSchema.required,
    globals,
    'false',
  );

  const [isResolvingIsDisabled, resolvedIsDisabled] = useJavascriptResolver(
    inputSchema.disabled,
    globals,
    'false',
  );

  const isValidAddress = useCallback((value: AddressInputAutocompleteData) => {
    const addressFormat =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      ADDRESS_FORMATS_BY_COUNTRY_CODE[value.countryCode] || DEFAULT_ADDRESS_FORMAT;
    const erroredFields: string[] = [];
    Object.entries(REQUIRED_FIELDS).forEach(([fieldKey, formRequirement]) => {
      const { fieldName, requiredCountries } = formRequirement;
      const fieldLabel =
        addressFormat.labels[fieldKey as AddressField] || DEFAULT_LABELS[fieldKey as AddressField];
      // isRequired check
      if (
        !value[fieldName] &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (isEmpty(requiredCountries) || requiredCountries.includes(value.countryCode))
      ) {
        erroredFields.push(fieldLabel);
      }
      // postal code match Regex check
      if (
        fieldName === 'postalCode' &&
        addressFormat.postalCodeRegex &&
        !addressFormat.postalCodeRegex.test(value.postalCode)
      ) {
        erroredFields.push(fieldLabel);
      }
    });

    return erroredFields.length === 0
      ? true
      : `Please enter a valid value for ${erroredFields
          .map((field) => field.toLowerCase())
          .join(', ')}`;
  }, []);

  if (isResolvingHidden || isResolvingOptions || isResolvingIsRequired || isResolvingIsDisabled) {
    return null;
  }

  // TODO(zixiao): better typing + consider moving isValidJson check + parse into JSON object logic to useJavascriptResolver level
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const isHidden: boolean = isValidJSON(resolvedIsHidden) ? JSON.parse(resolvedIsHidden) : false;
  const isDisabled: boolean = isValidJSON(resolvedIsDisabled)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      JSON.parse(resolvedIsDisabled)
    : false;
  const isRequired: boolean = isValidJSON(resolvedIsRequired)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      JSON.parse(resolvedIsRequired)
    : false;
  const options: { label: string; value: string }[] = isValidJSON(resolvedOptions)
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      JSON.parse(resolvedOptions)
    : [];

  if (isHidden) {
    return null;
  }

  let formField;
  switch (inputSchema.type) {
    case InputSchemaType.Dropdown:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <Select
              id={inputSchema.key}
              name={inputSchema.key}
              value={value}
              onValueChange={onChange}
              disabled={isSubmitting || isDisabled}
            >
              {options.map((option, idx) => (
                <Option key={idx} value={option?.value || ''}>
                  {option.label}
                </Option>
              ))}
            </Select>
          )}
        />
      );
      break;
    case InputSchemaType.Input:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <Input
              id={`form-account-action__${inputSchema.key}`}
              key={`form-account-action__${inputSchema.key}`}
              placeholder={inputSchema.placeholder}
              disabled={isSubmitting || isDisabled}
              onChange={onChange}
              value={value}
            />
          )}
        />
      );
      break;
    case InputSchemaType.MaskedInput:
      formField = (
        <MaskedInput
          {...register(
            inputSchema.key,
            isRequired
              ? {
                  required: 'Please enter a valid value.',
                }
              : undefined,
          )}
          id={`form-account-action__${inputSchema.key}`}
          key={`form-account-action__${inputSchema.key}`}
          disabled={isSubmitting || isDisabled}
          mask='000-000-000'
          placeholder='###-###-###'
        />
      );
      break;
    case InputSchemaType.Date:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => {
            const dateValues = value ? value.split('-') : [];
            const formattedValue = value
              ? new CalendarDate(
                  Number(dateValues[0]),
                  Number(dateValues[1]),
                  Number(dateValues[2]),
                )
              : null;
            const handleDateChange = (date: CalendarDate) => {
              if (date) {
                const dateObj = DateTime.local(date.year, date.month, date.day);
                if (dateObj.isValid) {
                  onChange(dateObj.toISODate());
                }
              }
            };
            return (
              <DateField
                aria-labelledby={`form-account-action__${inputSchema.key}`}
                disabled={isSubmitting || isDisabled}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onChange={handleDateChange}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value={formattedValue}
              />
            );
          }}
        />
      );
      break;
    case InputSchemaType.TextField:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <TextArea
              placeholder={inputSchema.placeholder}
              disabled={isSubmitting || isDisabled}
              onChange={onChange}
              value={value}
            />
          )}
        />
      );
      break;
    case InputSchemaType.Address:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { validate: isValidAddress } : undefined}
          render={({ field: { onChange, value } }) => (
            <AddressInputAutocomplete
              initialValues={value}
              disabled={isDisabled || isSubmitting}
              onChange={onChange}
            />
          )}
        />
      );
      break;
    case InputSchemaType.CheckBox:
      formField = (
        <>
          <Checkbox
            {...register(inputSchema.key)}
            label={inputSchema.label}
            required={isRequired}
            disabled={isSubmitting || isDisabled}
          />
          <Spacer height={1} />
        </>
      );
      break;
    case InputSchemaType.RadioGroup:
      formField = (
        <RadioGroup>
          {options.map((option) => (
            <Radio
              {...register(
                inputSchema.key,
                isRequired ? { required: 'Please select an option.' } : undefined,
              )}
              key={option.value}
              label={option.label}
              value={option.value || ''}
              disabled={isSubmitting || isDisabled}
            />
          ))}
        </RadioGroup>
      );
      break;
    case InputSchemaType.Phone:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <PhoneInput disabled={isSubmitting || isDisabled} value={value} onChange={onChange} />
          )}
        />
      );
      break;
    case InputSchemaType.Country:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <Select
              id={inputSchema.key}
              name={inputSchema.key}
              value={value}
              onValueChange={onChange}
              disabled={isSubmitting || isDisabled}
            >
              {sortBy(
                'label',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                Object.keys(COUNTRIES).map((code: CountryCode) => (
                  <Option key={code} value={code}>
                    {COUNTRIES[code]}
                  </Option>
                )),
              )}
            </Select>
          )}
        />
      );
      break;
    case InputSchemaType.USState:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <Select
              id={inputSchema.key}
              name={inputSchema.key}
              value={value}
              onValueChange={onChange}
              disabled={isSubmitting || isDisabled}
            >
              {sortBy(
                'label',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                Object.keys(US_STATES).map((code: keyof typeof US_STATES) => (
                  <Option key={US_STATES[code]} value={US_STATES[code]}>
                    {US_STATES[code]}
                  </Option>
                )),
              )}
            </Select>
          )}
        />
      );
      break;
    case InputSchemaType.USStateCode:
      formField = (
        <Controller
          control={control}
          name={inputSchema.key}
          rules={isRequired ? { required: 'Please enter a valid value.' } : undefined}
          render={({ field: { onChange, value } }) => (
            <Select
              id={inputSchema.key}
              name={inputSchema.key}
              value={value}
              onValueChange={onChange}
              disabled={isSubmitting || isDisabled}
            >
              {sortBy(
                'label',
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                Object.keys(US_STATES).map((code: keyof typeof US_STATES) => (
                  <Option key={US_STATES[code]} value={code}>
                    {US_STATES[code]}
                  </Option>
                )),
              )}
            </Select>
          )}
        />
      );
      break;
    default:
      // TODO(zixiao): assertNever(inputSchema.type) after introducing TriggerAccountInputEditor
      // as current json code editor will break the page during configuration time as user types with invalid input schema type
      break;
  }

  return (
    <>
      {/* Note that checkbox's label is included as part of the component itself */}
      {inputSchema.type !== 'checkbox' && (
        <Label size='md' htmlFor={`form-account-action__${inputSchema.key}`} weight='bold'>
          {inputSchema.label}
        </Label>
      )}
      <Spacer height={1} />
      {formField}
      {error && <FormError>{error.message}</FormError>}
      <Spacer height={3} />
    </>
  );
};

export default FormAccountAction;
